import { isHttpClientError } from 'core/infra/http/HttpClient'

import sentry from 'legacy/utils/sentry'

import { createAgnosticHttpClient } from 'auth/infra/createAgnosticHttpClient'

import {
  LATE_CHECK_IN_REASON_REQUIRED_ERROR_CODE,
  LateCheckInReasonRequiredError,
} from '../domain/LateCheckInReason'
import { ShipmentRepository } from '../infra/ShipmentRepository'

export type UpdateShipmentStatusParams = {
  shipmentUUID: string
  stopId: string | number
  type: string
  date: string
  time: string
  reasonCode?: string
  reasonNote?: string
  timezone?: string | null
}

export type UpdateShipmentStatusFactoryDependencies = {
  shipmentRepository: ShipmentRepository
}

function getLateCheckInReasonRequiredMessage(error: unknown): string | null {
  if (!isHttpClientError(error)) return null

  const data = error.response?.data as { code?: string; message?: string } | undefined

  if (data?.code !== LATE_CHECK_IN_REASON_REQUIRED_ERROR_CODE || !data.message) return null

  return data.message
}

export function updateShipmentStatusFactory({
  shipmentRepository,
}: UpdateShipmentStatusFactoryDependencies) {
  return async function submit(
    params: UpdateShipmentStatusParams,
    config?: { signal?: AbortSignal }
  ) {
    try {
      return await shipmentRepository.updateShipmentStatus(params, config)
    } catch (error) {
      const lateReasonMessage = getLateCheckInReasonRequiredMessage(error)

      if (lateReasonMessage) {
        throw new LateCheckInReasonRequiredError(lateReasonMessage, { cause: error as Error })
      }

      sentry.log(error)
      throw new Error('Could not update shipment status', { cause: error as Error })
    }
  }
}

export function updateShipmentStatus(
  params: UpdateShipmentStatusParams,
  config?: { signal?: AbortSignal }
) {
  const httpClient = createAgnosticHttpClient()
  const shipmentRepository = new ShipmentRepository({ httpClient })
  const submit = updateShipmentStatusFactory({ shipmentRepository })

  return submit(params, config)
}
